import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/auth';
import { setAccessToken, getAccessToken } from '../api/client';
import { User, LoginRequest, LoginResponse } from '../types';

export interface AuthContextType {
  user: User | null;
  login: (credentials: LoginRequest) => Promise<void>;
  completeLogin: (response: LoginResponse) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUser: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Refresh the access token
   */
  const refreshToken = useCallback(async () => {
    try {
      const response = await authApi.refreshToken();
      setAccessToken(response.access_token);

      // Fetch current user after refresh
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Token refresh failed:', err);
      setAccessToken(null);
      setUser(null);
      throw err;
    }
  }, []);

  /**
   * Initialize auth state on mount
   * Try to refresh token if we have a refresh token cookie
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Try to refresh token (will use httpOnly cookie)
        await refreshToken();
      } catch (err) {
        // No valid refresh token, user needs to login
        console.log('No valid session found');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [refreshToken]);

  /**
   * Login with email and password
   * Note: This method doesn't handle 2FA. For 2FA login flow,
   * use authApi.login directly and then completeLogin after verification.
   */
  const login = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(credentials);

      // Check if 2FA is required - if so, this won't have access_token
      if ('access_token' in response) {
        setAccessToken(response.access_token);
        // Fetch current user after login
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
      } else {
        // 2FA required - login flow needs to handle this
        throw new Error('2FA_REQUIRED');
      }
    } catch (err: any) {
      let errorMessage = 'Login failed';
      if (err.message === '2FA_REQUIRED') {
        errorMessage = '2FA_REQUIRED';
      } else {
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail[0].msg || 'Validation error';
        }
      }
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Complete login after 2FA verification
   * Called with the LoginResponse from verifyLogin2FA
   */
  const completeLogin = useCallback(async (response: LoginResponse) => {
    setIsLoading(true);
    setError(null);

    try {
      setAccessToken(response.access_token);
      // Fetch current user after login
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch (err: any) {
      let errorMessage = 'Failed to complete login';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      }
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setAccessToken(null);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh user data from API
   */
  const updateUser = useCallback(async () => {
    const currentUser = await authApi.getCurrentUser();
    setUser(currentUser);
  }, []);

  const isAuthenticated = !!user && !!getAccessToken();

  const value: AuthContextType = {
    user,
    login,
    completeLogin,
    logout,
    refreshToken,
    updateUser,
    isAuthenticated,
    isLoading,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
